import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { fetchOrder } from "../lib/api";
import { fmtARS, STATUS_LABEL } from "../lib/utils-app";
import { CheckCircle2, XCircle, Clock, Loader2, Gem } from "lucide-react";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const mpStatus = params.get("status"); // success | failure | pending
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    let cancel = false;
    async function load() {
      if (!orderId) return setLoading(false);
      try {
        const o = await fetchOrder(orderId);
        if (cancel) return;
        setOrder(o);
        // If still pending_payment and MP said success, retry after a moment (webhook might be arriving)
        if (o.status === "pending_payment" && mpStatus === "success" && tries < 4) {
          setTimeout(() => setTries((t) => t + 1), 2500);
        }
      } catch {
        setOrder(null);
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, tries]);

  const isSuccess = order?.status === "paid" || order?.status === "processing" || order?.status === "completed";
  const isFailure = mpStatus === "failure" || order?.status === "failed";

  const Icon = isSuccess ? CheckCircle2 : isFailure ? XCircle : Clock;
  const color = isSuccess ? "text-emerald-400" : isFailure ? "text-rose-400" : "text-amber-400";
  const title = isSuccess ? "¡Pago recibido!" : isFailure ? "Pago no completado" : "Pago pendiente";

  return (
    <div className="min-h-screen grid place-items-center px-4">
      <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 sm:p-8 text-center">
        {loading ? (
          <div className="flex items-center justify-center gap-2 text-neutral-400 py-10">
            <Loader2 className="w-5 h-5 animate-spin" /> Verificando pago...
          </div>
        ) : (
          <>
            <Icon className={`w-14 h-14 mx-auto ${color}`} />
            <h1 className="font-display text-4xl tracking-wider mt-3">{title}</h1>
            {order && (
              <>
                <p className="text-neutral-400 mt-2 text-sm">Estado: <span className="text-white">{STATUS_LABEL[order.status]}</span></p>
                <div className="mt-5 rounded-lg border border-white/10 p-4 text-left bg-black/40">
                  <div className="flex items-center gap-3 mb-3">
                    <Gem className="w-6 h-6 text-[#ff4500]" />
                    <div className="font-display text-2xl tracking-wider">
                      {order.diamonds ? `${order.diamonds.toLocaleString("es-AR")} DIAMANTES` : `${order.quantity || 1} × ${(order.product_name || "Pedido").toUpperCase()}`}
                    </div>
                  </div>
                  <Row label="Player ID" value={order.player_id} mono />
                  {order.player_nickname && <Row label="Nickname" value={order.player_nickname} />}
                  <Row label="Total" value={fmtARS(order.amount_ars)} />
                  <Row label="Pedido" value={order.order_id} mono />
                </div>
                {isSuccess && (
                  <p className="text-sm text-neutral-400 mt-4">
                    {order.diamonds ? "Estamos ejecutando tu recarga. En unos minutos verás los diamantes en tu cuenta." : "Pago acreditado. Estamos procesando tu pedido, te avisamos cuando esté listo."}
                    Podés seguir el estado en <Link to="/dashboard" className="text-[#ff4500] underline">Mis pedidos</Link>.
                  </p>
                )}
                {isFailure && (
                  <p className="text-sm text-neutral-400 mt-4">
                    El pago no pudo completarse. Intentá nuevamente desde <Link to="/" className="text-[#ff4500] underline">la página principal</Link>.
                  </p>
                )}
              </>
            )}
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Link to="/dashboard" className="btn-primary px-4 py-2 rounded-md" data-testid="result-dashboard-btn">Mis pedidos</Link>
              <Link to="/" className="px-4 py-2 rounded-md bg-white/5 hover:bg-white/10" data-testid="result-home-btn">Inicio</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-neutral-500">{label}</span>
      <span className={`text-white ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}
